import { api } from '@/lib/api'
import type {
  BeneficioItem,
  BeneficioVinculo,
  DpDashboard,
  FeriasResumo,
  FeriasSaldoPainel,
  FolhaResumo,
  Paginated,
  RescisaoResumo,
} from './types'

export async function fetchDpDashboard() {
  return api.get<DpDashboard>('/dp/dashboard/')
}

export async function fetchFolhas() {
  return api.get<{ sucesso?: boolean; dados: FolhaResumo[] }>('/dp/folhas/')
}

export async function fetchFerias(status?: string) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : ''
  return api.get<Paginated<FeriasResumo>>(`/dp/ferias/${qs}`)
}

export async function fetchFeriasSaldoPainel(status?: string) {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  params.set('por_pagina', '500')
  const qs = `?${params.toString()}`
  return api.get<FeriasSaldoPainel>(`/dp/ferias/saldo/painel/${qs}`)
}

export type FeriasSimularPayload = {
  salario: number
  dias?: number
  abono?: number
  dependentes?: number
}

export type FeriasCalculo = {
  valor_ferias?: number
  valor_terco?: number
  valor_abono?: number
  valor_inss?: number
  valor_irrf?: number
  valor_liquido?: number
  total_bruto?: number
  memoria_calculo?: { item: string; valor: number; sinal?: string; tipo?: string; lei?: string }[]
  [key: string]: unknown
}

export async function simularFerias(payload: FeriasSimularPayload) {
  return api.post<{ sucesso?: boolean; resultado: FeriasCalculo }>(
    '/dp/ferias/simular/',
    payload,
  )
}

export type FeriasAgendarPayload = {
  colaborador_id: string
  data_inicio: string
  dias_ferias: number
  dias_abono?: number
  periodo_inicio?: string | null
  periodo_fim?: string | null
  observacoes?: string
}

export async function agendarFerias(payload: FeriasAgendarPayload) {
  return api.post<{ sucesso?: boolean; dados: FeriasResumo; mensagem?: string }>(
    '/dp/ferias/',
    payload,
  )
}

export async function aprovarFerias(id: string) {
  return api.post<{ sucesso?: boolean; dados: FeriasResumo }>(`/dp/ferias/${id}/aprovar/`, {})
}

export async function recusarFerias(id: string, motivo = '') {
  return api.post<{ sucesso?: boolean; dados: FeriasResumo }>(`/dp/ferias/${id}/recusar/`, {
    motivo,
  })
}

export async function fetchRescisoes() {
  return api.get<Paginated<RescisaoResumo>>('/dp/rescisoes/')
}

export type RescisaoSimularPayload = {
  colaborador_id: string
  tipo: string
  data_demissao: string
  aviso_indenizado?: boolean
  dias_ferias_vencidas?: number
}

export type RescisaoCalculo = {
  totalLiquido?: number
  totalBruto?: number
  saldoSalario?: number
  avisoPrevioIndenizado?: number
  feriasProporcionais?: number
  feriasVencidas?: number
  decimoTerceiroProporcional?: number
  multaFGTS?: number
  inss?: number
  irrf?: number
  saldoFGTS?: number
  anosServico?: number
  mesesServico?: number
  detalhamento?: { diasAvisoPrevio?: number; mesesFeriasProporcionais?: number }
  memoria_calculo?: { item: string; valor: number; sinal?: string; tipo?: string; lei?: string }[]
  [key: string]: unknown
}

export async function simularRescisao(payload: RescisaoSimularPayload) {
  return api.post<{ sucesso?: boolean; resultado: RescisaoCalculo } & RescisaoCalculo>(
    '/dp/rescisoes/simular/',
    payload,
  )
}

export type RescisaoCreatePayload = RescisaoSimularPayload & {
  dados_wizard?: Record<string, unknown>
  finalizar?: boolean
  desligar_colaborador?: boolean
  motivo_detalhado?: string
  etapa_atual?: number
}

export async function createRescisao(payload: RescisaoCreatePayload) {
  return api.post<{
    sucesso?: boolean
    dados: RescisaoResumo
    resultado?: RescisaoCalculo
    mensagem?: string
  }>('/dp/rescisoes/', payload)
}

export async function fetchBeneficiosCatalogo(todos = false) {
  const qs = todos ? '?todos=1' : ''
  return api.get<{ sucesso?: boolean; dados: BeneficioItem[] }>(
    `/dp/beneficios/catalogo/${qs}`,
  )
}

export async function fetchBeneficiosVinculos() {
  return api.get<Paginated<BeneficioVinculo>>('/dp/beneficios/colaboradores/')
}

export async function fetchBeneficiosRelatorio() {
  return api.get<{ sucesso?: boolean; dados: Record<string, unknown> }>(
    '/dp/beneficios/relatorio/',
  )
}

export async function atribuirBeneficio(payload: {
  colaborador_id: string
  beneficio_id: string
  data_inicio?: string
  valor_empresa?: number | string
  valor_colab?: number | string
  observacoes?: string
}) {
  return api.post<{ sucesso?: boolean; dados: BeneficioVinculo }>('/dp/beneficios/atribuir/', payload)
}

export async function removerVinculoBeneficio(id: string) {
  return api.delete<{ sucesso?: boolean; mensagem?: string }>(`/dp/beneficios/${id}/`)
}

export async function alternarBeneficioStatus(id: string) {
  return api.patch<{ sucesso?: boolean; dados: BeneficioItem }>(
    `/dp/beneficios/catalogo/${id}/`,
    { alternar_status: true },
  )
}

export async function abrirFolha(competencia: string) {
  return api.post<{
    sucesso?: boolean
    periodo?: FolhaResumo
    criada?: boolean
    mensagem?: string
  }>('/dp/folhas/', { competencia })
}

export async function calcularFolha(id: string) {
  return api.post<{
    sucesso?: boolean
    periodo?: FolhaResumo
    mensagem?: string
  }>(`/dp/folhas/${id}/calcular/`, {})
}

export async function aprovarFolha(id: string) {
  return api.post<{
    sucesso?: boolean
    periodo?: FolhaResumo
    mensagem?: string
  }>(`/dp/folhas/${id}/aprovar/`, {})
}

export type DpNotificacao = {
  id: string
  tipo?: string
  titulo?: string
  mensagem?: string
  prioridade?: string
  status?: string
  lida?: boolean
  colaborador_nome?: string | null
  criado_em?: string | null
  data_prazo?: string | null
  [key: string]: unknown
}

export async function fetchDpNotificacoes(opts: {
  tipo?: string
  nao_lidas?: boolean
  prioridade?: string
} = {}) {
  const qs = new URLSearchParams()
  if (opts.tipo) qs.set('tipo', opts.tipo)
  if (opts.nao_lidas) qs.set('nao_lidas', '1')
  if (opts.prioridade) qs.set('prioridade', opts.prioridade)
  qs.set('por_pagina', '100')
  const suffix = qs.toString() ? `?${qs}` : ''
  return api.get<{
    sucesso?: boolean
    dados: DpNotificacao[]
    data?: DpNotificacao[]
    nao_lidas?: number
  }>(`/dp/notificacoes/${suffix}`)
}

export async function marcarNotificacaoLida(id: string) {
  return api.post<{ sucesso?: boolean; dados?: DpNotificacao }>(
    `/dp/notificacoes/${id}/ler/`,
    {},
  )
}

export async function verificarAlertasDp() {
  return api.post<{ sucesso?: boolean; criadas?: number }>('/dp/notificacoes/verificar/', {})
}

// ── Admissão / onboarding DP ──────────────────────────────────
export {
  fetchAdmissoes,
  fetchAdmissao,
  createAdmissao,
  createAdmissaoCompleta,
  patchAdmissao,
  sincronizarAdmissoes,
} from '@/features/rh/integracaoApi'
